/**
 * Pure resolver for `handleOpenAssetDrawer`.
 *
 * Lives outside `global.state.tsx` so it can be unit-tested without
 * pulling the whole GlobalStateProvider (and its test-setup module mock)
 * into the test.
 */
import type { PartialAsset } from '@/hooks/useAssets';

export interface HandleOpenAssetDrawerOptions {
  resolveToGroup?: boolean;
}

/**
 * Returns the asset as-is. The previous resolve-up logic replaced the
 * asset key with its parent Application key, but :Application nodes
 * don't carry the :Asset label so the drawer's :Asset query would find
 * nothing and spin forever. With the application_drawer gate removed
 * and HAS_MEMBER enrichment always-on, every parented asset was hitting
 * this — opening an #application# key that the drawer can't load.
 */
export function resolveAssetDrawerResource(
  asset: PartialAsset | undefined,
  _options?: HandleOpenAssetDrawerOptions
): PartialAsset | undefined {
  return asset;
}

/**
 * The asset key a note/comment should be written to from the asset drawer.
 *
 * Notes always land on the parent :Application so the unified thread reads
 * identically from the parent drawer and any member drawer (Decision A):
 *  - member observation → `group_key` is the parent `#application#…` key.
 *  - parent :Application opened directly → `group_key` is empty, so we fall
 *    back to the asset's own key (which already IS the parent).
 *
 * Extracted as a pure function so `AssetDrawer.handleComment` and its test
 * exercise the SAME rule — a future change to the rule breaks the test
 * instead of silently diverging from a hand-copied expression.
 */
export function selectNoteTargetKey(asset: {
  key: string;
  group_key?: string | null;
}): string {
  return asset.group_key || asset.key;
}
